/**
 * layerNav.js — navigation helpers for the cascade-modal layer stack (XOL-209).
 *
 * Exports:
 *   pushDrawer        — append (or replace) a drawer entry in the URL stack
 *   closeDrawer       — remove a named drawer entry from the URL stack
 *   replaceDrawerArgs — partial-merge args on an existing entry (tab/subtab switches)
 *   resolveDrawerHref — build canonical href WITHOUT navigating (for <a href> + cmd-click)
 *
 * All helpers guard against vue-router 2.x NavigationDuplicated via path string equality.
 * None return the router promise (vue-router 2.x doesn't guarantee one).
 */

import { parseDrawerPath, serialiseDrawerStack, findInStack } from '@/lib/drawerParser'

// ---------------------------------------------------------------------------
// Internal utilities
// ---------------------------------------------------------------------------

/**
 * Extract the basePath (everything up to and including the first 'd.' segment start)
 * from a full router path. Falls back to the router's current route path trimmed of
 * any existing drawer segments.
 *
 * We derive basePath from router.currentRoute.path by slicing off the drawer tail.
 */
function _extractBase (router) {
  var fullPath = router.currentRoute.path
  // Find the first /d. occurrence — that's where the drawer tail begins
  var idx = fullPath.indexOf('/d.')
  if (idx !== -1) return fullPath.slice(0, idx)
  return fullPath
}

/**
 * Parse the current drawer stack from the router's current path.
 */
function _currentStack (router) {
  var fullPath = router.currentRoute.path
  var idx = fullPath.indexOf('/d.')
  if (idx === -1) return []
  return parseDrawerPath(fullPath.slice(idx + 1))
}

/**
 * Build the full canonical path from a basePath + stack.
 */
function _buildPath (basePath, stack) {
  var tail = serialiseDrawerStack(stack)
  if (!tail) return basePath
  return basePath + '/' + tail
}

/**
 * Safe push — no-ops if the target path equals the current path.
 */
function _safePush (router, path) {
  if (router.currentRoute.path === path) return
  router.push({ path: path })
}

/**
 * Safe replace — no-ops if the target path equals the current path.
 */
function _safeReplace (router, path) {
  if (router.currentRoute.path === path) return
  router.replace({ path: path })
}

// ---------------------------------------------------------------------------
// PR-2 exports
// ---------------------------------------------------------------------------

/**
 * Build a canonical href for a drawer open without navigating.
 * Used for <a href> so cmd-click opens in a new tab.
 *
 * @param {Object} router  — vue-router instance
 * @param {{open: {[name]: Object}}} action  — same shape as pushDrawer
 * @returns {string}  canonical path string
 */
export function resolveDrawerHref (router, action) {
  var base = _extractBase(router)
  var stack = _currentStack(router).slice()
  var open = action.open || {}

  for (var name in open) {
    if (!Object.prototype.hasOwnProperty.call(open, name)) continue
    var newEntry = { name: name, args: open[name] }
    var existingIdx = -1
    for (var i = 0; i < stack.length; i++) {
      if (stack[i].name === name) { existingIdx = i; break }
    }
    if (existingIdx !== -1) {
      stack[existingIdx] = newEntry
    } else {
      stack.push(newEntry)
    }
  }

  return _buildPath(base, stack)
}

/**
 * Append a new drawer entry to the URL stack (or replace if same name already present).
 * Uses router.push — adds a history entry.
 *
 * @param {Object} router  — vue-router instance
 * @param {{open: {[name]: Object}}} action
 *   e.g. { open: { bill: { id: 789, tab: 'payment' } } }
 */
export function pushDrawer (router, action) {
  var target = resolveDrawerHref(router, action)
  _safePush(router, target)
}

/**
 * Remove the named drawer (and everything deeper after it) from the URL stack.
 * Uses router.push — adds a history entry.
 *
 * @param {Object} router  — vue-router instance
 * @param {string} name    — registered drawer name to remove
 */
export function closeDrawer (router, name) {
  var base = _extractBase(router)
  var stack = _currentStack(router).filter(function (e) { return e.name !== name })
  _safePush(router, _buildPath(base, stack))
}

/**
 * Partial-merge args on an existing stack entry (in-place tab/subtab switches).
 * Uses router.replace — does NOT add a history entry.
 *
 * @param {Object} router  — vue-router instance
 * @param {string} name    — registered drawer name to update
 * @param {Object} partialArgs  — e.g. { tab: 'finance', subtab: 'sharers' }
 */
export function replaceDrawerArgs (router, name, partialArgs) {
  var base = _extractBase(router)
  var stack = _currentStack(router).slice()
  var entry = findInStack(stack, name)

  if (!entry) return // defensive no-op

  // Merge: keep existing args, override with partialArgs
  var merged = Object.assign({}, entry.args, partialArgs)
  var idx = stack.indexOf(entry)
  stack[idx] = { name: name, args: merged }

  _safeReplace(router, _buildPath(base, stack))
}
